import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  LayoutDashboard,
  Users,
  DollarSign,
  Calendar,
  Map,
  User,
  Settings,
  Upload,
  FileText,
  BarChart3,
  Link2,
  Copy,
  Tag,
  ArrowRight,
} from 'lucide-react';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

interface CommandItem {
  id: string;
  label: string;
  category: 'Pages' | 'Contacts' | 'Donations' | 'Events';
  icon: React.ComponentType<{ className?: string }>;
  action: () => void;
}

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const navigateAndClose = useCallback(
    (path: string) => {
      navigate(path);
      onClose();
    },
    [navigate, onClose],
  );

  const allItems = useMemo((): CommandItem[] => {
    const pages: CommandItem[] = [
      {
        id: 'page-dashboard',
        label: 'Dashboard',
        category: 'Pages',
        icon: LayoutDashboard,
        action: () => navigateAndClose('/'),
      },
      {
        id: 'page-contacts',
        label: 'Contacts',
        category: 'Pages',
        icon: Users,
        action: () => navigateAndClose('/contacts'),
      },
      {
        id: 'page-donations',
        label: 'Donations',
        category: 'Pages',
        icon: DollarSign,
        action: () => navigateAndClose('/donations'),
      },
      {
        id: 'page-events',
        label: 'Events',
        category: 'Pages',
        icon: Calendar,
        action: () => navigateAndClose('/events'),
      },
      {
        id: 'page-counties',
        label: 'Counties',
        category: 'Pages',
        icon: Map,
        action: () => navigateAndClose('/counties'),
      },
      {
        id: 'page-declarations',
        label: 'Declarations',
        category: 'Pages',
        icon: FileText,
        action: () => navigateAndClose('/declarations'),
      },
      {
        id: 'page-reports',
        label: 'Reports',
        category: 'Pages',
        icon: BarChart3,
        action: () => navigateAndClose('/reports'),
      },
      {
        id: 'page-donation-reports',
        label: 'Donation Reports',
        category: 'Pages',
        icon: BarChart3,
        action: () => navigateAndClose('/donation-reports'),
      },
      {
        id: 'page-users',
        label: 'Users',
        category: 'Pages',
        icon: User,
        action: () => navigateAndClose('/users'),
      },
      {
        id: 'page-settings',
        label: 'Settings',
        category: 'Pages',
        icon: Settings,
        action: () => navigateAndClose('/settings'),
      },
      {
        id: 'page-import',
        label: 'Import',
        category: 'Pages',
        icon: Upload,
        action: () => navigateAndClose('/import'),
      },
      {
        id: 'page-duplicates',
        label: 'Duplicates',
        category: 'Pages',
        icon: Copy,
        action: () => navigateAndClose('/duplicates'),
      },
      {
        id: 'page-tags',
        label: 'Tags',
        category: 'Pages',
        icon: Tag,
        action: () => navigateAndClose('/tags'),
      },
      {
        id: 'page-integrations',
        label: 'Integrations',
        category: 'Pages',
        icon: Link2,
        action: () => navigateAndClose('/integrations'),
      },
    ];

    const trimmed = query.trim();

    const searchItems: CommandItem[] = trimmed
      ? [
          {
            id: 'search-contacts',
            label: `Search contacts for "${trimmed}"`,
            category: 'Contacts',
            icon: Users,
            action: () => navigateAndClose(`/contacts?search=${encodeURIComponent(trimmed)}`),
          },
          {
            id: 'search-donations',
            label: `Search donations for "${trimmed}"`,
            category: 'Donations',
            icon: DollarSign,
            action: () => navigateAndClose(`/donations?search=${encodeURIComponent(trimmed)}`),
          },
          {
            id: 'search-events',
            label: `Search events for "${trimmed}"`,
            category: 'Events',
            icon: Calendar,
            action: () => navigateAndClose(`/events?search=${encodeURIComponent(trimmed)}`),
          },
        ]
      : [];

    return [...searchItems, ...pages];
  }, [query, navigateAndClose]);

  const filteredItems = useMemo(() => {
    if (!query.trim()) return allItems;
    const lower = query.toLowerCase();
    return allItems.filter((item) => item.label.toLowerCase().includes(lower));
  }, [allItems, query]);

  const groupedItems = useMemo(() => {
    const groups: Record<string, CommandItem[]> = {};
    const categoryOrder: CommandItem['category'][] = ['Contacts', 'Donations', 'Events', 'Pages'];

    for (const item of filteredItems) {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }
      groups[item.category].push(item);
    }

    const ordered: { category: string; items: CommandItem[] }[] = [];
    for (const cat of categoryOrder) {
      if (groups[cat] && groups[cat].length > 0) {
        ordered.push({ category: cat, items: groups[cat] });
      }
    }
    return ordered;
  }, [filteredItems]);

  // Reset state when opening/closing
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      // Small delay so the DOM is mounted before focusing
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Clamp activeIndex when filtered results change
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    if (active) {
      active.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const totalItems = filteredItems.length;
      if (totalItems === 0) return;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          setActiveIndex((prev) => (prev + 1) % totalItems);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          setActiveIndex((prev) => (prev - 1 + totalItems) % totalItems);
          break;
        }
        case 'Enter': {
          e.preventDefault();
          const item = filteredItems[activeIndex];
          if (item) {
            item.action();
          }
          break;
        }
        case 'Escape': {
          e.preventDefault();
          onClose();
          break;
        }
      }
    },
    [filteredItems, activeIndex, onClose],
  );

  if (!open) return null;

  // Compute the flat index for rendering active state
  let flatIndex = 0;

  return createPortal(
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Palette container */}
      <div className="fixed inset-0 flex items-start justify-center pt-[15vh] px-4">
        <div
          className="relative w-full max-w-xl bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
          onKeyDown={handleKeyDown}
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 border-b border-slate-200">
            <Search className="w-5 h-5 text-slate-400 flex-shrink-0" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              className="flex-1 h-12 bg-transparent text-sm text-slate-900 placeholder-slate-400 outline-none"
              placeholder="Search pages, contacts, donations..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search command palette"
              aria-autocomplete="list"
              aria-controls="command-palette-list"
            />
            <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 bg-slate-100 rounded border border-slate-200">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div
            ref={listRef}
            id="command-palette-list"
            role="listbox"
            className="max-h-80 overflow-y-auto py-2"
          >
            {filteredItems.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">
                No results found for &ldquo;{query}&rdquo;
              </div>
            ) : (
              groupedItems.map((group) => (
                <div key={group.category}>
                  <div className="px-4 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    {group.category}
                  </div>
                  {group.items.map((item) => {
                    const currentIndex = flatIndex;
                    flatIndex++;
                    const isActive = currentIndex === activeIndex;
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        role="option"
                        aria-selected={isActive}
                        data-active={isActive}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                          isActive
                            ? 'bg-slate-100 text-slate-900'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                        onClick={() => item.action()}
                        onMouseEnter={() => setActiveIndex(currentIndex)}
                      >
                        <Icon
                          className={`w-4 h-4 flex-shrink-0 ${
                            isActive ? 'text-slate-700' : 'text-slate-400'
                          }`}
                          aria-hidden="true"
                        />
                        <span className="flex-1 truncate">{item.label}</span>
                        {isActive && (
                          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" aria-hidden="true" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer hint */}
          <div className="flex items-center gap-4 px-4 py-2 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-400">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 font-medium bg-white rounded border border-slate-200">&uarr;</kbd>
              <kbd className="px-1 py-0.5 font-medium bg-white rounded border border-slate-200">&darr;</kbd>
              <span className="ml-0.5">to navigate</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 font-medium bg-white rounded border border-slate-200">Enter</kbd>
              <span className="ml-0.5">to select</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 font-medium bg-white rounded border border-slate-200">Esc</kbd>
              <span className="ml-0.5">to close</span>
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
